import React, { useCallback, useContext, useEffect, useState } from 'react'

import { Context } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, object } from 'prop-types'

import SubscriptionEntryPoints from 'Pods/Shared/Constants/subscriptionEntryPoints'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import AdultContentModal from 'Pods/Shared/LazyAdultContentModal'
import ModelCard from 'Pods/Shared/ModelCard/ModelCard'

import { EMPTY_ARRAY, EMPTY_OBJECT, NOOP } from 'Utils/stableDefaults'
import useBreakpoint from 'Utils/useBreakpoint'

import { getCardCount } from './Constants'
import PromoPanel from './PromoPanel'
import Skeleton from './Skeleton'
import useUpsellBlockTracking from './useUpsellBlockTracking'
import useUpsellModels from '../../Hooks/useUpsellModels'

// ModelCard maps `searchQuery` (which falls back to itemListId) into GA4 item_list_id and
// item_list_name, so this is what separates upsell card clicks from ordinary listing results.
const UPSELL_LIST_NAME = 'Free filter subscription upsell'

const HIDDEN = 'hidden'
const SKELETON = 'skeleton'
const VISIBLE = 'visible'

// Single source of truth for what this component renders. The impression is the ticket's
// eligible-search denominator, so the tracking flag and the render branch must never disagree.
function resolveBlockState({ isFreeFilterActive, isEnabled, hasPageConfig, isEligible, isLoading, modelCount }) {
  if (!isFreeFilterActive || !isEnabled) return HIDDEN
  // Pre-hydration optimistic skeleton: avoids CLS for eligible users on the high-intent free
  // filter path. Subscribers may see a brief flash; acceptable vs. layout shift on hydration.
  if (!hasPageConfig) return SKELETON
  if (!isEligible) return HIDDEN
  if (isLoading) return SKELETON

  return modelCount > 0 ? VISIBLE : HIDDEN
}

function UpsellBlock({ cart = EMPTY_OBJECT, slowUpsellBlockEnabled = false, premiumListingLabelEnabled = false }) {
  const context = useContext(Context)
  const {
    selectedFilters,
    cardSizeSetting,
    cardVisibilitySettings,
    browserAttributes,
    setSelectedFilters: handleFilterChange,
    viewerIsAdult = false,
    setViewerIsAdult = NOOP,
    isSimilarSearchPossible = false,
  } = context || {}
  const pageConfig = usePageConfig()
  const breakpoint = useBreakpoint()
  const [showGallery, setShowGallery] = useState()
  const [galleryToShow, setGalleryToShow] = useState()
  const [isAdultContentModalVisible, toggleAdultModal] = useState(false)
  const [cartItemIds, setCartItemIds] = useState(cart.cartItemIds || EMPTY_ARRAY)
  const [wishListItems, setWishListItems] = useState(EMPTY_ARRAY)

  useEffect(() => {
    setCartItemIds(cart.cartItemIds || EMPTY_ARRAY)
  }, [cart])

  // Mirror List: defer opening the gallery to the next render so the previously
  // open preview tears down first (ModelCard reads showGallery synchronously).
  useEffect(() => {
    setShowGallery(galleryToShow)
  }, [galleryToShow])

  const handlePreviewClick = useCallback((previewProps) => {
    setShowGallery(undefined)
    setGalleryToShow({ ...previewProps })
  }, [])

  const handleCloseGallery = useCallback(() => setShowGallery(undefined), [])
  const showAdultModal = useCallback(() => toggleAdultModal(true), [])
  const hideAdultModal = useCallback(() => toggleAdultModal(false), [])

  const hasFiltersLoaded = !!selectedFilters?.pricing
  const freeFromContext = !!selectedFilters?.pricing?.free
  const isFreeFilterActive = hasFiltersLoaded ? freeFromContext : !!browserAttributes?.searchingFree
  const keywords = selectedFilters?.keywords || ''
  const suggestedKeywords = selectedFilters?.suggested_keywords || ''

  const upsellBlockEnabled = pageConfig ? !!pageConfig.subscriptionListingUpsellBlock : slowUpsellBlockEnabled
  const isActiveSubscriber = !!pageConfig?.isActiveSubscriber
  const isEligible = upsellBlockEnabled && !isActiveSubscriber

  const { models, isLoading } = useUpsellModels({
    enabled: isFreeFilterActive && isEligible,
    keywords,
    suggestedKeywords,
  })
  const blockState = resolveBlockState({
    isFreeFilterActive,
    isEnabled: upsellBlockEnabled,
    hasPageConfig: !!pageConfig,
    isEligible,
    isLoading,
    modelCount: models.length,
  })
  const { trackCtaClick, trackCardClick } = useUpsellBlockTracking({
    rendered: blockState === VISIBLE,
    searchTerm: keywords,
    suggestedKeywords,
  })

  if (blockState === SKELETON) return <Skeleton />
  if (blockState === HIDDEN) return null

  const cardCount = getCardCount(breakpoint)
  const visibleModels = models.slice(0, cardCount)

  // Capture phase: ModelCard's own handler navigates synchronously, so a bubbling listener would
  // run after the page had already started unloading.
  const handleCardClickCapture = (event) => {
    if (event.target.closest('.upsell-block__promo-slot, .card-3d-model__action-buttons')) return

    const card = event.target.closest('[data-model-id]')
    if (!card) return

    const cards = [...event.currentTarget.querySelectorAll('[data-model-id]')]
    trackCardClick({ itemId: Number(card.dataset.modelId), index: cards.indexOf(card) })
  }

  return (
    <div className="upsell-block col-12" onClickCapture={handleCardClickCapture}>
      <div className="card-3d-model upsell-block__promo-slot">
        <PromoPanel onCtaClick={trackCtaClick} />
      </div>
      {visibleModels.map((model, index) => (
        <ModelCard
          key={model.id}
          {...model}
          cart={cart}
          modelIndex={index}
          listIndex={index}
          modelsPerRow={cardCount + 1}
          lastModelIndex={visibleModels.length - 1}
          viewerIsAdult={viewerIsAdult}
          showGallery={showGallery}
          showAdultModal={showAdultModal}
          onPreviewClick={handlePreviewClick}
          onCloseGallery={handleCloseGallery}
          isSimilarSearchPossible={isSimilarSearchPossible}
          gaList={UPSELL_LIST_NAME}
          itemListId={SubscriptionEntryPoints.LISTING_FREE_FILTER_UPSELL}
          subscriptionFlowEnabled
          isUpsell
          premiumListingLabelEnabled={premiumListingLabelEnabled}
          currentSelectedFileTypes={EMPTY_ARRAY}
          setSelectedFilters={handleFilterChange || NOOP}
          trackFilterChange={NOOP}
          cardVisibilitySettings={cardVisibilitySettings || EMPTY_OBJECT}
          cardSizeSetting={cardSizeSetting || 'medium'}
          cartItemIds={cartItemIds}
          setCartItemIds={setCartItemIds}
          wishListItems={wishListItems}
          setWishListItems={setWishListItems}
        />
      ))}
      {isAdultContentModalVisible && (
        <AdultContentModal setViewerIsAdult={setViewerIsAdult} hideAdultModal={hideAdultModal} />
      )}
    </div>
  )
}

UpsellBlock.propTypes = {
  cart: object,
  slowUpsellBlockEnabled: bool,
  premiumListingLabelEnabled: bool,
}

export default UpsellBlock
